import { errorReportText, errorTicketPrefill, formatErrorTime, type ErrorContext } from '@supply/contracts'
import { AlertTriangle, Check, ChevronDown, Copy, Home, LifeBuoy, RotateCcw } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { SupportTicketDialog, type SupportPrefill } from '@/modules/support/SupportTicketDialog'

/**
 * Tela de erro: o que quebrou, onde, quando, e o caminho para o chamado.
 *
 * Dependência mínima de propósito — nada de sessão, router, tema ou toast:
 * ela aparece justamente quando um deles pode ter quebrado. O chamado usa o
 * mesmo `SupportTicketDialog` do topo, só que pré-preenchido.
 */
export function ErrorScreen({
  ctx,
  variant,
  onRetry,
  onHome,
}: {
  ctx: ErrorContext
  variant: 'tela-cheia' | 'area'
  onRetry: () => void
  onHome?: (() => void) | undefined
}) {
  const heading = React.useRef<HTMLHeadingElement>(null)
  const details = React.useMemo(() => errorReportText(ctx), [ctx])
  const [copy, setCopy] = React.useState<'idle' | 'ok' | 'falhou'>('idle')
  const [prefill, setPrefill] = React.useState<SupportPrefill | null>(null)

  React.useEffect(() => heading.current?.focus(), [])

  async function copyDetails() {
    try {
      await navigator.clipboard.writeText(details)
      setCopy('ok')
    } catch {
      setCopy('falhou')
    }
  }

  function openTicket() {
    setPrefill(errorTicketPrefill(ctx))
  }

  const fullScreen = variant === 'tela-cheia'

  return (
    <div
      className={cn(
        'flex justify-center',
        fullScreen ? 'min-h-dvh items-center bg-background px-4 py-10 text-foreground' : 'px-1 py-6 sm:py-10',
      )}
    >
      <section aria-labelledby="erro-titulo" className="w-full max-w-2xl space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive" aria-hidden>
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0 space-y-1">
            <h1 id="erro-titulo" ref={heading} tabIndex={-1} className="text-lg font-semibold leading-tight outline-none">
              {fullScreen ? 'O Supply encontrou um erro e parou' : 'Esta tela encontrou um erro'}
            </h1>
            <p className="text-sm text-muted-foreground">
              O que já estava salvo continua salvo. Tente de novo; se o erro voltar, abra um chamado — os detalhes
              abaixo vão junto para o time de Sistemas.
            </p>
          </div>
        </div>

        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <p className="break-words font-mono text-sm text-destructive [overflow-wrap:anywhere]">{ctx.message}</p>
        </div>

        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <Info label="Tela">{ctx.route}</Info>
          <Info label="Horário">{formatErrorTime(ctx.at)}</Info>
          <Info label="Versão">{ctx.build || 'não informada'}</Info>
        </dl>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button onClick={onRetry}>
            <RotateCcw aria-hidden />
            Tentar novamente
          </Button>
          <Button variant="outline" onClick={onHome ?? (() => window.location.assign('/'))}>
            <Home aria-hidden />
            Voltar ao início
          </Button>
          <Button variant="outline" onClick={openTicket} aria-haspopup="dialog">
            <LifeBuoy aria-hidden />
            Abrir chamado sobre este erro
          </Button>
        </div>

        {/* O buffer do index.html: inclui o que aconteceu antes de o React subir. */}
        <Collapsible className="rounded-md border border-border">
          <div className="px-3 py-2">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="group -ml-2">
                <ChevronDown className="transition-transform group-data-[state=open]:rotate-180" aria-hidden />
                Console
                <span className="font-mono tabular-nums text-muted-foreground">({ctx.log.length})</span>
              </Button>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            {ctx.log.length === 0 ? (
              <p className="border-t border-border px-3 py-3 text-sm text-muted-foreground">Nada registrado no console.</p>
            ) : (
              <ol className="max-h-80 overflow-auto border-t border-border bg-muted/40 p-3 font-mono text-[12px] leading-relaxed tabular-nums">
                {ctx.log.map((entry, i) => (
                  <li key={i} className="break-words [overflow-wrap:anywhere]">
                    <span className="text-muted-foreground">[{formatErrorTime(entry.at).slice(-8)}]</span>{' '}
                    <span className={entry.kind === 'aviso' ? 'text-warning' : 'text-destructive'}>{entry.kind}</span>:{' '}
                    {entry.message}
                  </li>
                ))}
              </ol>
            )}
          </CollapsibleContent>
        </Collapsible>

        <Collapsible className="rounded-md border border-border">
          <div className="flex items-center gap-2 px-3 py-2">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="group -ml-2 mr-auto">
                <ChevronDown className="transition-transform group-data-[state=open]:rotate-180" aria-hidden />
                Detalhes técnicos
              </Button>
            </CollapsibleTrigger>
            <Button variant="outline" size="sm" onClick={() => void copyDetails()}>
              {copy === 'ok' ? <Check aria-hidden /> : <Copy aria-hidden />}
              {copy === 'ok' ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          {copy === 'falhou' && (
            <p role="status" className="px-3 pb-2 text-[13px] text-destructive">
              Não deu para copiar. Abra os detalhes e selecione o texto.
            </p>
          )}
          <CollapsibleContent>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words border-t border-border bg-muted/40 p-3 font-mono text-[12px] leading-relaxed tabular-nums [overflow-wrap:anywhere]">
              {details}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      </section>

      <SupportTicketDialog
        open={prefill !== null}
        onOpenChange={(open) => !open && setPrefill(null)}
        prefill={prefill ?? undefined}
      />
    </div>
  )
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="break-words font-mono tabular-nums [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}
